<template>
  <div class="visual-screen">
    <!-- 顶部标题 -->
    <div class="header">
      <h1 class="title">🎓 基于 K-Means 的就业难成因分析大屏</h1>
      <div class="subtitle">数据来源：spark-jiuye 数据库 | 实时分析引擎</div>
    </div>

    <div class="main-content" v-loading="loading">
      
      <!-- 左侧：核心结论与聚类 -->
      <div class="column left">
        <!-- 核心结论卡片 -->
        <div class="panel conclusion-panel">
          <h3 class="panel-title">💡 核心洞察：为什么就业难？</h3>
          <div class="conclusion-text">
            {{ analysisData.difficulty_reason || '正在分析数据...' }}
          </div>
          <div class="key-metrics">
            <div class="metric-item">
              <span class="label">求职者平均期望</span>
              <span class="value highlight">{{ analysisData.salary_gap?.avg_want || 0 }}k</span>
            </div>
            <div class="metric-item">
              <span class="label">岗位平均提供</span>
              <span class="value">{{ analysisData.salary_gap?.avg_offer || 0 }}k</span>
            </div>
            <div class="metric-item">
              <span class="label">薪资缺口</span>
              <span class="value danger">{{ analysisData.salary_gap?.gap || 0 }}k</span>
            </div>
          </div>
        </div>

        <!-- K-Means 聚类散点图 (模拟展示簇分布) -->
        <div class="panel">
          <h3 class="panel-title">📊 K-Means 求职者聚类分布</h3>
          <div ref="chartCluster" class="chart-box"></div>
        </div>

        <!-- 避雷岗位 -->
        <div class="panel">
          <h3 class="panel-title">⚠️ 高频“不感兴趣”岗位 Top5</h3>
          <el-table :data="analysisData.avoid_jobs" size="small" style="width: 100%" :show-header="false">
            <el-table-column prop="name" label="岗位"></el-table-column>
            <el-table-column prop="money" label="薪资" width="100"></el-table-column>
            <el-table-column prop="no_times" label="被拒次数" width="100" align="right">
              <template #default="scope">
                <span style="color: red; font-weight: bold">{{ scope.row.no_times }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 中间：供需对比与地图 -->
      <div class="column center">
        <!-- 供需薪资对比 -->
        <div class="panel">
          <h3 class="panel-title">💰 期望薪资 vs 实际供给</h3>
          <div ref="chartSalary" class="chart-box-large"></div>
        </div>

        <!-- 岗位 mismtach 对比 -->
        <div class="panel">
          <h3 class="panel-title">⚖️ 热门岗位：我想做 vs 公司在招</h3>
          <div ref="chartMismatch" class="chart-box-large"></div>
        </div>
      </div>

      <!-- 右侧：画像分析 -->
      <div class="column right">
        <!-- 学历要求 -->
        <div class="panel">
          <h3 class="panel-title">🎓 企业学历门槛分布</h3>
          <div ref="chartEdu" class="chart-box"></div>
        </div>

        <!-- 城市热度 -->
        <div class="panel">
          <h3 class="panel-title">🏙️ 求职者期望城市 Top5</h3>
          <div ref="chartCity" class="chart-box"></div>
        </div>

        <!-- 经验要求 -->
        <div class="panel">
          <h3 class="panel-title">⏳ 经验要求分析 (模拟)</h3>
           <div ref="chartYear" class="chart-box"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as echarts from 'echarts';

export default {
  name: 'VisualScreen',
  data() {
    return {
      loading: true,
      analysisData: {},
      charts: {}
    };
  },
  mounted() {
    this.fetchData();
    window.addEventListener('resize', this.resizeCharts);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeCharts);
    Object.values(this.charts).forEach(chart => chart.dispose());
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        // 请求 Python 后端接口
        const res = await axios.get('http://localhost:5000/api/employment-analysis');
        if (res.data.code === 200) {
          this.analysisData = res.data.data;
          this.$nextTick(() => {
            this.initCharts();
          });
        } else {
          this.$message.error(res.data.msg);
        }
      } catch (error) {
        console.error(error);
        this.$message.error('连接分析服务失败，请检查 Python 后端是否启动');
        // 模拟数据用于演示（如果后端没起）
        this.useMockData(); 
      } finally {
        this.loading = false;
      }
    },

    useMockData() {
      // 仅在后端未启动时展示效果
      this.analysisData = {
        difficulty_reason: "（演示模式）分析发现，约 45% 的求职者处于'高期望高投递'状态。期望薪资平均高于市场供给 3.5k，导致匹配周期拉长。",
        salary_gap: { avg_want: 12.5, avg_offer: 9.0, gap: 3.5 },
        kmeans_clusters: [
          { cluster_id: 0, avg_salary: 8, avg_apply_count: 2, population: 30 },
          { cluster_id: 1, avg_salary: 15, avg_apply_count: 12, population: 45 },
          { cluster_id: 2, avg_salary: 10, avg_apply_count: 5, population: 25 }
        ],
        edu_requirement: { "本科及以上": 60, "大专及以上": 30, "不限": 10 },
        job_mismatch: {
          top_want: { "Java开发": 40, "产品经理": 30, "UI设计": 20 },
          top_offer: { "销售顾问": 50, "客服专员": 40, "Java开发": 20 }
        },
        city_hotspot: { "北京": 40, "上海": 35, "深圳": 30, "杭州": 20, "成都": 15 },
        avoid_jobs: [{name: "电话销售", money: "4k-6k", no_times: 120}]
      };
      this.$nextTick(() => this.initCharts());
    },

    initCharts() {
      this.initClusterChart();
      this.initSalaryChart();
      this.initMismatchChart();
      this.initEduChart();
      this.initCityChart();
      this.initYearChart();
    },

    initClusterChart() {
      const chart = echarts.init(this.$refs.chartCluster);
      this.charts.cluster = chart;
      
      const data = this.analysisData.kmeans_clusters || [];
      // 构造散点数据：x=薪资，y=投递次数，size=人数
      const scatterData = data.map(item => [
        item.avg_salary, 
        item.avg_apply_count, 
        item.population,
        item.cluster_id
      ]);

      const option = {
        tooltip: {
          formatter: function (param) {
            return `簇${param.data[3]}: 期望${param.data[0]}k<br/>投递${param.data[1]}次<br/>人数:${param.data[2]}`;
          }
        },
        xAxis: { name: '平均期望薪资(k)', splitLine: { show: false } },
        yAxis: { name: '平均投递次数', splitLine: { lineStyle: { type: 'dashed' } } },
        series: [{
          type: 'scatter',
          symbolSize: function (data) { return Math.sqrt(data[2]) * 8; },
          data: scatterData,
          itemStyle: {
            color: function(params) {
              const colors = ['#5470c6', '#ee6666', '#91cc75'];
              return colors[params.data[3] % 3];
            },
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.5)'
          }
        }]
      };
      chart.setOption(option);
    },

    initSalaryChart() {
      const chart = echarts.init(this.$refs.chartSalary);
      this.charts.salary = chart;
      const gap = this.analysisData.salary_gap || {};
      
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: ['求职者期望', '企业提供', '缺口'] },
        yAxis: { type: 'value', name: '薪资 (k)' },
        series: [{
          data: [
            { value: gap.avg_want || 0, itemStyle: { color: '#5470c6' } },
            { value: gap.avg_offer || 0, itemStyle: { color: '#91cc75' } },
            { value: Math.abs(gap.gap || 0), itemStyle: { color: '#ee6666' } }
          ],
          type: 'bar',
          barWidth: '40%',
          label: { show: true, position: 'top' }
        }]
      };
      chart.setOption(option);
    },

    initMismatchChart() {
      const chart = echarts.init(this.$refs.chartMismatch);
      this.charts.mismatch = chart;
      
      const want = this.analysisData.job_mismatch?.top_want || {};
      const offer = this.analysisData.job_mismatch?.top_offer || {};
      
      // 合并所有岗位名
      const allJobs = Array.from(new Set([...Object.keys(want), ...Object.keys(offer)]));
      
      const option = {
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['求职者想做的', '企业在招的'], textStyle: { color: '#fff' } },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: allJobs, axisLabel: { interval: 0, rotate: 30 } },
        yAxis: { type: 'value', name: '数量/热度' },
        series: [
          {
            name: '求职者想做的',
            type: 'bar',
            data: allJobs.map(job => want[job] || 0),
            itemStyle: { color: '#fac858' }
          },
          {
            name: '企业在招的',
            type: 'bar',
            data: allJobs.map(job => offer[job] || 0),
            itemStyle: { color: '#73c0de' }
          }
        ]
      };
      chart.setOption(option);
    },

    initEduChart() {
      const chart = echarts.init(this.$refs.chartEdu);
      this.charts.edu = chart;
      const eduData = this.analysisData.edu_requirement || {};
      
      const option = {
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          data: Object.keys(eduData).map(key => ({ value: eduData[key], name: key })),
          label: { color: '#fff' }
        }]
      };
      chart.setOption(option);
    },

    initCityChart() {
      const chart = echarts.init(this.$refs.chartCity);
      this.charts.city = chart;
      const cityData = this.analysisData.city_hotspot || {};
      
      const option = {
        tooltip: { trigger: 'axis' },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: { type: 'category', data: Object.keys(cityData), axisLabel: { rotate: 30 } },
        yAxis: { type: 'value' },
        series: [{
          data: Object.values(cityData),
          type: 'bar',
          itemStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{offset: 0, color: '#83bff6'}, {offset: 1, color: '#188df0'}]) }
        }]
      };
      chart.setOption(option);
    },

    initYearChart() {
      // 模拟经验数据，因为数据库字段解析较复杂，此处做示意
      const chart = echarts.init(this.$refs.chartYear);
      this.charts.year = chart;
      const option = {
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie',
          radius: '50%',
          data: [
            { value: 40, name: '1-3年' },
            { value: 30, name: '3-5年' },
            { value: 20, name: '不限' },
            { value: 10, name: '5年以上' }
          ],
          label: { color: '#fff' }
        }]
      };
      chart.setOption(option);
    },

    resizeCharts() {
      Object.values(this.charts).forEach(chart => chart.resize());
    }
  }
};
</script>

<style scoped>
.visual-screen {
  width: 100%;
  height: 100vh;
  background-color: #0b1120;
  color: #fff;
  font-family: "Noto Sans", system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-image: radial-gradient(circle at 50% 50%, #1b263b 0%, #0b1120 100%);
}

.header {
  height: 80px;
  text-align: center;
  border-bottom: 1px solid rgba(59, 130, 246, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.title {
  font-size: 28px;
  margin: 0;
  background: linear-gradient(to right, #60a5fa, #34d399);
  -webkit-background-clip: text;
  color: transparent;
  font-weight: bold;
  letter-spacing: 2px;
}

.subtitle {
  font-size: 12px;
  color: #93c5fd;
  margin-top: 5px;
}

.main-content {
  flex: 1;
  display: grid;
  grid-template-columns: 28% 44% 28%;
  gap: 15px;
  padding: 15px;
  overflow: hidden;
}

.column {
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;
}

.panel {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  padding: 15px;
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 16px;
  color: #e2e8f0;
  margin: 0 0 10px 0;
  padding-left: 10px;
  border-left: 4px solid #3b82f6;
  font-weight: 600;
}

.conclusion-panel {
  background: rgba(59, 130, 246, 0.15);
  border-color: #3b82f6;
}

.conclusion-text {
  font-size: 14px;
  line-height: 1.6;
  color: #cbd5e1;
  margin-bottom: 15px;
  flex: 1;
}

.key-metrics {
  display: flex;
  justify-content: space-around;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px;
  border-radius: 6px;
}

.metric-item {
  text-align: center;
}

.metric-item .label {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.metric-item .value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.value.highlight { color: #34d399; }
.value.danger { color: #f87171; }

.chart-box {
  height: 200px;
  width: 100%;
}

.chart-box-large {
  height: 280px;
  width: 100%;
}

/* 滚动条样式 */
::-webkit-scrollbar { width: 6px; }
::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
</style>